// Плашка «нет сети» над нижними вкладками (Figma «Snackbar»: поля 8 сверху и снизу, по бокам 24 или своё экрана, жёлтая карточка r16 высотой 64, иконка 24, заголовок 15/20, подпись 12/16). Источник — onlineManager TanStack Query (события online/offline):
// пока сети нет, запросы стоят на паузе, а при её возвращении он сам перезапрашивает устаревшее.
// Слабый мобильный интернет: браузер считает, что сеть есть, а запросы падают на сети (ApiError 'network') — плашка та же,
// пока любой запрос не ответит; упавшие запросы открытых экранов тем временем перезапрашиваются сами (HQ-1060, AC-3/AC-4).
// Экран с кэшем может заменить текст своим (Figma «Матч · сыгран · Нет интернета»: «Нет сети. Показан счёт от 14:32» + «Повторить»).
import { type RefObject, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { focusManager, onlineManager, type QueryClient, useQueryClient } from '@tanstack/react-query';
import { ApiError } from '../api/client';
import infoIcon from '../assets/states/info.svg';
import { t } from '../i18n/i18n';
import styles from './OfflineBanner.module.css';
import { setOfflineBannerText } from './Toast';

export interface OfflineNote { title: string; action?: { label: string; onPress: () => void } }

let note: OfflineNote | null = null;
const listeners = new Set<() => void>();
const subscribe = (cb: () => void) => { listeners.add(cb); return () => { listeners.delete(cb); }; };
const setNote = (next: OfflineNote | null) => { note = next; listeners.forEach((cb) => cb()); };

/** Пока экран открыт, плашка «нет сети» говорит его текстом; null — обычный текст. */
export function useOfflineNote(next: OfflineNote | null) {
  const title = next?.title;
  const label = next?.action?.label;
  const onPress = next?.action?.onPress;
  useEffect(() => {
    if (!title) return;
    setNote({ title, action: label && onPress ? { label, onPress } : undefined });
    return () => setNote(null);
  }, [title, label, onPress]);
}

/* Полноэкранная заглушка «нет сети» (ErrorView без compact) сама говорит то же, что плашка (Figma 1073:23495: нет сети и нет
   данных — только заглушка). Пока хоть одна такая заглушка смонтирована, плашка скрыта; ушли на экран с данными или сеть
   вернулась — заглушки нет, и плашка ведёт себя как прежде (1073:23371). Счётчик, а не флаг: при смене экрана новая
   заглушка монтируется раньше, чем снимается старая (HQ-2225). */
let placeholders = 0;
const placeholderListeners = new Set<() => void>();
const subscribePlaceholders = (cb: () => void) => { placeholderListeners.add(cb); return () => { placeholderListeners.delete(cb); }; };
const placeholderShown = () => placeholders > 0;
const bumpPlaceholders = (delta: number) => { placeholders += delta; placeholderListeners.forEach((cb) => cb()); };

/** Пока компонент смонтирован и active истинно, на экране полноэкранная заглушка «нет сети» — плашка скрыта. */
export function useOfflinePlaceholder(active: boolean) {
  useEffect(() => {
    if (!active) return;
    bumpPlaceholders(1);
    return () => bumpPlaceholders(-1);
  }, [active]);
}

/* Боковые поля плашки по умолчанию — 24 (.wrap: var(--space-6)), как Snackbar матч-центра 772:33133 / 33136 / 33139, матча
   844:15849 и настроек 772:28491 (24 · 345 · 24 на 393). Экран, в макете которого плашка на полях 8 (новости 772:30734 …),
   ставит в своё дерево метку <span hidden data-offline-gutter="8" /> — поля меняет CSS (:has), без импорта отсюда (HQ-2213). */

/** Пауза между перезапросами упавших экранов, пока сеть «есть», но не отвечает. */
export const NETWORK_PROBE_MS = 15_000;

const isNetworkError = (error: unknown) => error instanceof ApiError && error.kind === 'network';

/** Последний исход запросов: упал на сети — true, любой успешный ответ — false (onChange — только на смену). Пока true —
 *  раз в NETWORK_PROBE_MS перезапрашиваем открытые экраны, чей запрос упал на сети: сеть вернулась — данные обновятся и
 *  плашка уйдёт без перезагрузки. Пока приложение свёрнуто (focusManager.isFocused() ложно), тик пропускаем, как и весь
 *  остальной опрос (refetchIntervalInBackground: false); фокус вернулся — перезапросит следующий тик (HQ-1441).
 *  Возвращает отписку. */
export function watchNetwork(client: QueryClient, onChange: (failing: boolean) => void): () => void {
  let failing = false;
  let timer: ReturnType<typeof setInterval> | null = null;
  const set = (next: boolean) => {
    if (next === failing) return;
    failing = next;
    if (timer) { clearInterval(timer); timer = null; }
    if (next) {
      timer = setInterval(() => {
        if (!focusManager.isFocused()) return;
        void client.refetchQueries({ type: 'active', predicate: (q) => q.state.status === 'error' && isNetworkError(q.state.error) });
      }, NETWORK_PROBE_MS);
    }
    onChange(next);
  };
  const off = client.getQueryCache().subscribe((event) => {
    if (event.type !== 'updated') return;
    const { action } = event;
    if (action.type === 'failed' || action.type === 'error') {
      if (isNetworkError(action.error)) set(true);
    } else if (action.type === 'success') set(false);
  });
  return () => { off(); if (timer) clearInterval(timer); };
}

function useNetworkFailing(): boolean {
  const client = useQueryClient();
  const [failing, setFailing] = useState(false);
  useEffect(() => watchNetwork(client, setFailing), [client]);
  return failing;
}

/** Высота плашки (с полями) — в --offline-h на <html> и метка data-offline-banner (HQ-1112 v4): по ним область содержимого
 *  (App.module.css .content) резервирует снизу место под плашку, и она не накрывает содержимое ни при каком шрифте.
 *  Высота следит за плашкой (ResizeObserver: шрифт, язык, свой текст экрана). Возвращает снятие: метки и переменной нет. */
export function markOfflineHeight(el: HTMLElement, root: HTMLElement = document.documentElement): () => void {
  const write = () => root.style.setProperty('--offline-h', `${Math.ceil(el.getBoundingClientRect().height)}px`);
  root.setAttribute('data-offline-banner', '');
  write();
  const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(write);
  ro?.observe(el);
  return () => { ro?.disconnect(); root.removeAttribute('data-offline-banner'); root.style.removeProperty('--offline-h'); };
}

function useOfflineHeightMark(ref: RefObject<HTMLElement | null>, shown: boolean) {
  useEffect(() => {
    const el = ref.current;
    if (!shown || !el) return;
    return markOfflineHeight(el);
  }, [ref, shown]);
}

/** Тост «нет сети» не нужен, пока то же говорит плашка (HQ-1659 v2) или скрывшая её заглушка (HQ-2225). */
function useOfflineToastMute(offline: boolean) {
  useEffect(() => {
    if (!offline) return;
    setOfflineBannerText(t('mc_offline'));
    return () => setOfflineBannerText(null);
  }, [offline]);
}

export function OfflineBanner() {
  const online = useSyncExternalStore((cb) => onlineManager.subscribe(cb), () => onlineManager.isOnline());
  const custom = useSyncExternalStore(subscribe, () => note);
  const failing = useNetworkFailing();
  const placeholder = useSyncExternalStore(subscribePlaceholders, placeholderShown, placeholderShown);
  const offline = !online || failing;
  const shown = offline && !placeholder;
  const ref = useRef<HTMLDivElement>(null);
  useOfflineToastMute(offline);
  useOfflineHeightMark(ref, shown);
  if (!shown) return null;
  return (
    <div ref={ref} className={styles.wrap}>
      <div className={styles.banner} role="status">
        <img className={styles.icon} src={infoIcon} alt="" width={24} height={24} />
        <span className={styles.text}>
          <span className={styles.title}>{custom?.title ?? t('mc_offline')}</span>
          {custom?.action
            ? <button className={`${styles.sub} ${styles.action}`} onClick={custom.action.onPress}>{custom.action.label}</button>
            : !custom && <span className={styles.sub}>{t('mc_offline_check')}</span>}
        </span>
      </div>
    </div>
  );
}
